import { Component, effect, OnInit, signal, inject } from '@angular/core'
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router'
import { mergeMap } from 'rxjs/operators'
import { AuthService } from '@shared/services/auth.service'
import { ConfigService } from '@shared/services/config.service'
import { SharedService } from '@shared/services/shared.service'
import { Store } from '@ngrx/store'
import { Observable, Subscription, timer } from 'rxjs'
import { AppActions } from '@store/app.actions'
import { AppSelectors } from '@store/app.selectors'
import { ApiService } from '@shared/services/api.service'
import { environment } from '@environments/environment'
import { LoanService } from '@shared/services/loan.service'
import { NgOptimizedImage, AsyncPipe, UpperCasePipe } from '@angular/common'
import { LoadingComponent } from '@shared/components/loading/loading.component'
import { TranslatePipe } from '@ngx-translate/core'
import { ServiceNotification } from '@shared/interfaces'
import { GlobalService } from '@shared/services/global.service'

@Component({
  selector: 'app-admin-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrls: ['./dashboard-layout.component.scss'],
  imports: [
    NgOptimizedImage,
    RouterLink,
    LoadingComponent,
    RouterLinkActive,
    RouterOutlet,
    AsyncPipe,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class DashboardLayoutComponent implements OnInit {
  private router = inject(Router)
  api = inject(ApiService)
  config = inject(ConfigService)
  auth = inject(AuthService)
  sharedService = inject(SharedService)
  store = inject(Store)
  globalService = inject(GlobalService)

  environment = environment
  loading = signal(false)
  serviceNotification = signal<ServiceNotification | null>(null)
  cardMenuVisible$: Observable<boolean>
  unreadMessagesTimer: Subscription | undefined

  constructor() {
    const store = this.store

    this.cardMenuVisible$ = store.select(AppSelectors.cardMenuVisibleSelector)
    effect(() => {
      const customer = this.auth.customer()
      if (customer) {
        this.unreadMessagesTimer = timer(0, 30000)
          .pipe(mergeMap(() => this.api.getUnreadMessagesNumber()))
          .subscribe({
            next: ({ count }) => this.globalService.unreadMessagesNumber.set(count),
            error: () => {},
          })

        if (this.auth.isNewCustomer) {
          this.api.getCreditCardLoan().subscribe({
            next: ({ loan }) => {
              if (loan?.id) {
                this.store.dispatch(AppActions.showCardMenu())
              }
              this.store.dispatch(
                LoanService.isPaymentAllowed(loan)
                  ? AppActions.enablePaymentButton()
                  : AppActions.disablePaymentButton(),
              )
            },
            error: async () => {
              console.error(`Could not get active loan for user ${customer.id}`)
              await this.router.navigateByUrl('/decision-rejected')
            },
          })
        }
      } else {
        this.cancelTimer()
        this.store.dispatch(AppActions.hideCardMenu())
      }
    })
  }

  ngOnInit() {
    this.api.getServiceNotification().subscribe(response => this.serviceNotification.set(response))
  }

  cancelTimer() {
    if (this.unreadMessagesTimer) {
      this.unreadMessagesTimer.unsubscribe()
    }
  }

  async logout() {
    if (this.loading()) {
      return
    }
    this.loading.set(true)
    this.cancelTimer()
    await this.router.navigate(['/dashboard-visa/login'], { queryParams: { logout: true } })
  }
}
